import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { ACDLError } from '../../models/error.model';
import { IDatasetsStore } from './datasets.store';
import { IDatasetsDetailsStore } from './datasetsDetails.store';
import { IImagesUploaderService } from './imagesUpload.service';

export interface IUpdateDatasetService {
  uploadImages(datasetId: string): Promise<void | ACDLError>;
  markDatasetAsUploaded(datasetId: string, uploadedItemsCount: number): void;
}

export namespace IUpdateDatasetService {
  export const $: interfaces.ServiceIdentifier<IUpdateDatasetService> = Symbol('UPDATE_DATASET_SERVICE');
}

@injectable()
export class UpdateDatasetService implements IUpdateDatasetService {
  constructor(
    @inject(IImagesUploaderService.$) private readonly imagesService: IImagesUploaderService,
    @inject(IDatasetsDetailsStore.$) private readonly datasetsDetailsStore: IDatasetsDetailsStore,
    @inject(IDatasetsStore.$) private readonly datasetsStore: IDatasetsStore
  ) {
    makeObservable(this);
    this.imagesService.onUploadFinishedSuccessfully.push((datasetId: string) => {
      this.markDatasetAsUploaded(datasetId);
    });
    this.imagesService.onUploadAborted.push((datasetId: string) => {
      this.markDatasetAsUploaded(datasetId);
    });
    this.imagesService.onDatasetUploadProgress.push((datasetId: string, imageSize: number) => {
      this.updateProgress(datasetId, imageSize);
    });
  }

  @action
  async uploadImages(dataSetId: string): Promise<void> {
    const dataSetName = this.datasetsDetailsStore.details.name;

    if (this.datasetsDetailsStore.imagesToUploadFromFile.length > 0) {
      await this.imagesService.addDatasetToQueueAsync(this.datasetsDetailsStore.imagesToUploadFromFile, dataSetId, dataSetName);
    }

    this.datasetsDetailsStore.markAsUploading(dataSetId);
    this.datasetsStore.markAsUploading(dataSetId);

    this.datasetsDetailsStore.imagesToUploadFromFile.clear();
    this.datasetsDetailsStore.imagesRejectedFromFile.clear();
  }

  @action.bound
  markDatasetAsUploaded(dataSetId: string): void {
    this.datasetsDetailsStore.markAsUploaded(dataSetId);
    this.datasetsStore.markAsUploaded(dataSetId);
  }

  @action.bound
  updateProgress(dataSetId: string, imageSize: number): void {
    this.datasetsDetailsStore.incrementProgress(dataSetId, imageSize);
    this.datasetsStore.incrementProgress(dataSetId, imageSize);
  }
}
